import { useFetcher } from "react-router";

import type { ColorTheme } from "@/theme/color-theme";
import { useColorTheme } from "@/theme/theme";
import { Button } from "@/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/ui/tooltip";

interface ColorThemeOption {
  label: string;
  swatch: string;
  value: ColorTheme;
}

const COLOR_THEME_OPTIONS = [
  {
    label: "Meadow",
    swatch:
      "linear-gradient(135deg, oklch(0.58 0.13 153) 0 50%, oklch(0.75 0.12 77) 50% 100%)",
    value: "meadow",
  },
  {
    label: "Ocean",
    swatch:
      "linear-gradient(135deg, oklch(0.58 0.13 245) 0 50%, oklch(0.75 0.12 195) 50% 100%)",
    value: "ocean",
  },
  {
    label: "Berry",
    swatch:
      "linear-gradient(135deg, oklch(0.58 0.13 300) 0 50%, oklch(0.75 0.12 350) 50% 100%)",
    value: "berry",
  },
  {
    label: "Amber",
    swatch:
      "linear-gradient(135deg, oklch(0.58 0.13 70) 0 50%, oklch(0.75 0.12 35) 50% 100%)",
    value: "amber",
  },
] as const satisfies readonly ColorThemeOption[];

// Offer the Cents FYI palettes without coupling them to light or dark mode.
export function ColorThemeControl() {
  const colorTheme = useColorTheme();
  const fetcher = useFetcher();
  const currentOption =
    COLOR_THEME_OPTIONS.find((option) => option.value === colorTheme) ??
    COLOR_THEME_OPTIONS[0];

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label={`Choose color theme. Current theme: ${currentOption.label}`}
              size="icon"
              type="button"
              variant="ghost"
            >
              <span
                aria-hidden
                className="border-foreground/15 size-4 rounded-full border shadow-xs"
                style={{ background: currentOption.swatch }}
              />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={5}>
          Choose color theme
        </TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="end" className="w-44" sideOffset={5}>
        <DropdownMenuLabel>Color theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={colorTheme}
          onValueChange={(value) => {
            void fetcher.submit(
              { colorTheme: value },
              { action: "/color-theme", method: "POST" },
            );
          }}
        >
          {COLOR_THEME_OPTIONS.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              className="min-h-10"
              disabled={fetcher.state !== "idle"}
              value={option.value}
            >
              <span
                aria-hidden
                className="border-foreground/15 size-4 rounded-full border shadow-xs"
                style={{ background: option.swatch }}
              />
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
